/**
 * FilterPanel Component
 * Config-driven Popover with quick filters, addable filter rows, and "+ Add filter" menu.
 * All state is owned by the parent — this component is purely presentational.
 */

import { useRef, useState } from 'react';
import {
  Popover,
  Button,
  Group,
  Text,
  Stack,
  ActionIcon,
  MultiSelect,
  Select,
  Badge,
  Box,
  UnstyledButton,
  Divider,
  type MantineSize,
} from '@mantine/core';
import { Filter, X, Plus, ChevronDown } from 'react-feather';
import classes from './FilterPanel.module.css';

// ── Types ──────────────────────────────────────────────────────────────────

export interface FilterFieldConfig {
  key: string;
  label: string;
  type: 'multiselect' | 'select';
  options: { value: string; label: string }[];
}

export interface QuickFilterConfig {
  key: string;
  label: string;
}

export interface ActiveFilterRow {
  key: string;
  values: string[];
}

export interface FilterPanelProps {
  /** Available quick filter chips */
  quickFilters?: QuickFilterConfig[];
  /** Currently active quick filter keys */
  activeQuickFilters?: string[];
  /** Toggle a quick filter on/off */
  onQuickFilterToggle?: (key: string) => void;

  /** All possible addable filter fields */
  filterFields: FilterFieldConfig[];
  /** Currently active filter rows */
  activeFilters: ActiveFilterRow[];
  /** Add a new filter row by field key */
  onFilterAdd: (key: string) => void;
  /** Change values for an existing filter row */
  onFilterChange: (key: string, values: string[]) => void;
  /** Remove a filter row */
  onFilterRemove: (key: string) => void;

  /** Clear all filters (quick + addable) */
  onClearAll: () => void;

  /** Mantine size for trigger button and inner controls (default "sm") */
  size?: MantineSize;
}

export function FilterPanel({
  quickFilters = [],
  activeQuickFilters = [],
  onQuickFilterToggle,
  filterFields,
  activeFilters,
  onFilterAdd,
  onFilterChange,
  onFilterRemove,
  onClearAll,
  size = 'sm',
}: FilterPanelProps) {
  const [opened, setOpened] = useState(false);
  // The clicked button unmounts, so keep focus in the panel: Escape then closes it and Tab goes on.
  const dropdownRef = useRef<HTMLDivElement>(null);
  // The clear button unmounts too, so focus goes back to the trigger.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  // Each open is a new visit: the content remounts, so no list or select stays open from the last one.
  const [visit, setVisit] = useState(0);

  const totalActive = activeFilters.filter((af) => af.values.length > 0).length
    + activeQuickFilters.length;

  // Fields not yet added as active rows
  const availableFields = filterFields.filter(
    (f) => !activeFilters.some((af) => af.key === f.key),
  );

  const handleClear = () => {
    dropdownRef.current?.focus();
    onClearAll();
    setAddMenuOpen(false);
  };

  const handleDismiss = () => {
    handleClear();
    setOpened(false);
  };

  const handleRemoveFilter = (key: string) => {
    dropdownRef.current?.focus();
    onFilterRemove(key);
  };

  const handleAddField = (key: string) => {
    dropdownRef.current?.focus();
    onFilterAdd(key);
    setAddMenuOpen(false);
  };

  return (
    <Box pos="relative" display="inline-flex">
      {opened && (
        <div
          className={classes.overlay}
          onClick={() => setOpened(false)}
          onMouseDown={(e) => e.preventDefault()}
        />
      )}
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      withinPortal
      shadow="md"
      trapFocus={false}
      closeOnClickOutside={false}
    >
      <Popover.Target>
        <Button
          ref={triggerRef}
          data-filter-trigger
          variant="outline"
          color="gray"
          bg="white"
          style={{ borderColor: '#E8E6E3' }}
          size={size}
          leftSection={<Filter size={16} />}
          // Space for the clear button, which sits beside the trigger so no button is inside a button
          rightSection={
            totalActive > 0 ? (
              <Group gap={4} wrap="nowrap">
                <Badge size="sm" circle color="medleyGreen">
                  {totalActive}
                </Badge>
                <Box w={22} />
              </Group>
            ) : undefined
          }
          onClick={() => {
            // The add list belongs to one visit: the panel opens with it closed.
            setAddMenuOpen(false);
            if (!opened) setVisit((v) => v + 1);
            setOpened((o) => !o);
          }}
        >
          Filter
        </Button>
      </Popover.Target>

      <Popover.Dropdown ref={dropdownRef} tabIndex={-1} className={classes.dropdown}>
        <Stack gap="sm" key={visit}>
          {/* Header */}
          <Group justify="space-between">
            <Text size="sm" fw={600}>
              Filters
            </Text>
            {totalActive > 0 && (
              <Text
                size="sm"
                c="dimmed"
                style={{ cursor: 'pointer' }}
                onClick={handleClear}
              >
                Clear
              </Text>
            )}
          </Group>

          {/* Quick Filters */}
          {quickFilters.length > 0 && (
            <Group gap="xs" wrap="wrap">
              {quickFilters.map((qf) => {
                const isActive = activeQuickFilters.includes(qf.key);
                return (
                  <Button
                    key={qf.key}
                    variant={isActive ? 'filled' : 'default'}
                    color={isActive ? 'medleyGreen' : undefined}
                    size={size}
                    onClick={() => onQuickFilterToggle?.(qf.key)}
                  >
                    {qf.label}
                  </Button>
                );
              })}
            </Group>
          )}

          {/* Active Filter Rows */}
          {activeFilters.map((af) => {
            const fieldConfig = filterFields.find((f) => f.key === af.key);
            if (!fieldConfig) return null;

            return (
              <Group key={af.key} gap="xs" wrap="nowrap" className={classes.filterRow}>
                <Text size="sm" fw={500} className={classes.filterLabel}>
                  {fieldConfig.label}
                </Text>
                {fieldConfig.type === 'multiselect' ? (
                  <MultiSelect
                    data={fieldConfig.options}
                    value={af.values}
                    onChange={(values) => onFilterChange(af.key, values)}
                    size={size}
                    placeholder="Select..."
                    hidePickedOptions={false}
                    className={classes.filterInput}
                    comboboxProps={{ withinPortal: false }}
                  />
                ) : (
                  <Select
                    data={fieldConfig.options}
                    value={af.values[0] || null}
                    onChange={(value) => onFilterChange(af.key, value ? [value] : [])}
                    size={size}
                    placeholder="Select..."
                    className={classes.filterInput}
                    comboboxProps={{ withinPortal: false }}
                  />
                )}
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="md"
                  aria-label="Remove filter"
                  onClick={() => handleRemoveFilter(af.key)}
                >
                  <X size={16} />
                </ActionIcon>
              </Group>
            );
          })}

          {/* Add Filter — inline list (no portal Menu to avoid closing popover) */}
          {availableFields.length > 0 && (
            <>
              {addMenuOpen ? (
                <Stack gap={0}>
                  <Divider mb="xs" />
                  {availableFields.map((field) => (
                    <UnstyledButton
                      key={field.key}
                      className={classes.addFilterOption}
                      onClick={() => handleAddField(field.key)}
                    >
                      <Text size="sm">{field.label}</Text>
                    </UnstyledButton>
                  ))}
                </Stack>
              ) : (
                <Button
                  variant="subtle"
                  color="gray"
                  size={size}
                  leftSection={<Plus size={16} />}
                  rightSection={<ChevronDown size={14} />}
                  onClick={() => setAddMenuOpen(true)}
                >
                  Add filter
                </Button>
              )}
            </>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
      {totalActive > 0 && (
        <ActionIcon
          variant="transparent"
          size="sm"
          color="gray"
          aria-label="Clear filters"
          onClick={() => {
            handleDismiss();
            triggerRef.current?.focus();
          }}
          style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}
        >
          <X size={14} />
        </ActionIcon>
      )}
    </Box>
  );
}

export default FilterPanel;
